import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout'
import type { Appearance } from '@stripe/stripe-js'
import { type FormEvent, useMemo, useState } from 'react'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getStripe, STRIPE_PUBLISHABLE_KEY } from '@/lib/stripe/browser'

/**
 * Stripe's Payment Element mounted on a custom-UI Checkout Session
 * (LG-1033). The Session comes from `audition.createCheckout`; this owns
 * only what happens between "secret in hand" and "paid": card fields, a
 * promo code box, the total, and confirm.
 *
 * On success it calls `onPaid` and nothing else — the stage change comes
 * from the webhook, not from the browser claiming it paid, so the parent
 * refetches rather than trusting this callback for anything but UI.
 */
export function InlinePayment({
  clientSecret,
  onPaid,
  submitLabel,
}: {
  clientSecret: string
  onPaid: () => void
  /** Defaults to "Pay <total>" — the lane pages say what the payment IS. */
  submitLabel?: string
}) {
  const { resolvedTheme } = useTheme()
  // Only the parts of the canvas the element can see: the same stone
  // palette as index.css, in hex because Appearance does not read CSS
  // variables. Memoised — the provider re-inits Elements on a new object.
  const appearance = useMemo<Appearance>(
    () =>
      resolvedTheme === 'dark'
        ? {
            theme: 'night',
            variables: { ...SHARED_VARIABLES, ...DARK_VARIABLES },
          }
        : {
            theme: 'stripe',
            variables: { ...SHARED_VARIABLES, ...LIGHT_VARIABLES },
          },
    [resolvedTheme],
  )
  const options = useMemo(
    () => ({ clientSecret, elementsOptions: { appearance } }),
    [clientSecret, appearance],
  )

  if (!STRIPE_PUBLISHABLE_KEY) {
    // A missing key is a deploy fault, not a customer one — say so rather
    // than show a form that never loads.
    return (
      <p role="alert" className="text-sm text-destructive">
        Payments aren't set up in this environment yet.
      </p>
    )
  }

  return (
    <CheckoutElementsProvider stripe={getStripe()} options={options}>
      <PaymentForm onPaid={onPaid} submitLabel={submitLabel} />
    </CheckoutElementsProvider>
  )
}

const SHARED_VARIABLES: Appearance['variables'] = {
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  borderRadius: '8px',
  colorDanger: '#dc2626',
}
const LIGHT_VARIABLES: Appearance['variables'] = {
  colorPrimary: '#292524',
  colorBackground: '#ffffff',
  colorText: '#1c1917',
  colorTextSecondary: '#79716b',
}
const DARK_VARIABLES: Appearance['variables'] = {
  colorPrimary: '#e7e5e4',
  colorBackground: '#1c1917',
  colorText: '#fafaf9',
  colorTextSecondary: '#a6a09b',
}

function PaymentForm({
  onPaid,
  submitLabel,
}: {
  onPaid: () => void
  submitLabel?: string
}) {
  const state = useCheckoutElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (state.type === 'loading') {
    return <p className="text-sm text-muted-foreground">Loading payment…</p>
  }
  if (state.type === 'error') {
    return (
      <p role="alert" className="text-sm text-destructive">
        {`We couldn't load the payment form — ${state.error.message}`}
      </p>
    )
  }
  const { checkout } = state

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    // `if_required`: cards authenticate in-page; only a redirect-based
    // method would leave, and the session allows none.
    let result: Awaited<ReturnType<typeof checkout.confirm>>
    try {
      result = await checkout.confirm({ redirect: 'if_required' })
    } catch {
      // A rejected promise (network drop, an expired Session) never lands in
      // `result.error` — without this the button stays on "Processing…"
      // forever and the applicant cannot try again.
      setError("We couldn't complete the payment — please try again.")
      setSubmitting(false)
      return
    }
    if (result.type === 'error') {
      setError(result.error.message)
      setSubmitting(false)
      return
    }
    onPaid()
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement
        options={{
          // Link and wallets are session-disabled (card only); `never`
          // here keeps the element from reserving space for them anyway.
          wallets: { applePay: 'never', googlePay: 'never' },
        }}
      />
      <PromoCode checkout={checkout} />
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">Total</span>
        <span className="font-semibold">{checkout.total.total.amount}</span>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button
        type="submit"
        className="w-full"
        disabled={submitting || !checkout.canConfirm}
      >
        {submitting
          ? 'Processing…'
          : (submitLabel ?? `Pay ${checkout.total.total.amount}`)}
      </Button>
    </form>
  )
}

type Checkout = Extract<
  ReturnType<typeof useCheckoutElements>,
  { type: 'success' }
>['checkout']

/**
 * Promo codes go through the Session (`allow_promotion_codes` stays on),
 * so the discount is Stripe's and the webhook sees the reduced amount —
 * the app never does pricing math.
 */
function PromoCode({ checkout }: { checkout: Checkout }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const applied = checkout.discountAmounts?.find((d) => d.promotionCode)

  // The same shape the confirm path uses: a rejected promise never lands in
  // `result.type === 'error'`, so without the `finally` a dropped request
  // strands `busy` on true and the button is disabled for good — with
  // nothing on screen saying why.
  const remove = async () => {
    setBusy(true)
    setError(null)
    let result: Awaited<ReturnType<typeof checkout.removePromotionCode>>
    try {
      result = await checkout.removePromotionCode()
    } catch {
      setError("We couldn't remove that code — please try again.")
      return
    } finally {
      setBusy(false)
    }
    // A refused removal comes back as a value, not a throw. Without this the
    // discount row simply stays put with nothing saying the click did
    // anything — the same silence the `catch` above exists to break.
    if (result.type === 'error') setError(result.error.message)
  }

  if (applied) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span>
            <span className="font-medium">{applied.promotionCode}</span>
            <span className="text-muted-foreground">
              {` — ${applied.amount} off`}
            </span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={remove}
          >
            Remove
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    )
  }

  const apply = async () => {
    const trimmed = code.trim()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    let result: Awaited<ReturnType<typeof checkout.applyPromotionCode>>
    try {
      result = await checkout.applyPromotionCode(trimmed)
    } catch {
      setError("We couldn't apply that code — please try again.")
      return
    } finally {
      setBusy(false)
    }
    if (result.type === 'error') {
      setError(
        result.error.code === 'invalidCode'
          ? "That code isn't valid."
          : result.error.message,
      )
      return
    }
    setCode('')
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Input
          aria-label="Promo code"
          placeholder="Promo code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            // Enter in this box applies the code; it must not submit the
            // payment form around it.
            if (e.key === 'Enter') {
              e.preventDefault()
              void apply()
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={busy || !code.trim()}
          onClick={apply}
        >
          Apply
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
